import { Fragment, useEffect, useState } from "react"
import {
  StyledInput,
  StyledLabel,
  StyledInputNumberFormItem,
  PickerPopupStyle,
  StyledPickerView,
  PickerSelectedMask,
  PickerViewContainer,
  PickerHeader,
  PickerHeaderTop,
  PickerHeaderBottom,
  DoneButton,
  PickerTextFeet,
  PickerTextInches,
  StyledPickerViewInches,
  HeightWheelPickerPopup,
} from "./styled"
import {
  MAX_BODY_FEET,
  MAX_BODY_INCHES,
  MIN_BODY_FEET,
} from "utils/heightValidator"
import { DEFAULT_FEET, DEFAULT_INCHES } from "utils/heightConverter"

const PopupIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="37"
      height="4"
      viewBox="0 0 37 4"
      fill="rgba(255, 255, 255, 0.20)"
    >
      <rect
        x="0.5"
        width="36"
        height="4"
        rx="2"
        fill="rgba(255, 255, 255, 0.20)"
      />
    </svg>
  )
}

interface IProps {
  feet: number
  inches: number
  isError?: boolean
  disabled?: boolean
  onChange: (feet: number, inches: number) => void
}

const feetColumns = Array.from(Array(9))
  .fill(0)
  .reduce((options, _, value) => {
    if (value < MIN_BODY_FEET || value > MAX_BODY_FEET) {
      return options
    }

    return [
      ...options,
      {
        label: value.toString(),
        value: value,
      },
    ]
  }, [])

const HeightInput = (props: IProps) => {
  const { feet, inches, onChange, isError, disabled } = props
  const [visible, setVisible] = useState(false)
  const [localFeet, setLocalFeet] = useState(feet)
  const [localInches, setLocalInches] = useState(inches)

  const inchesColumn = Array.from(Array(12))
    .fill(0)
    .reduce((options, _, value) => {
      const maxInches = localFeet === MAX_BODY_FEET ? MAX_BODY_INCHES : 11

      if (value > maxInches) {
        return options
      }

      return [
        ...options,
        {
          label: value.toString(),
          value: value,
        },
      ]
    }, [])

  useEffect(() => {
    if (feet === 0 && inches === 0) {
      setLocalFeet(DEFAULT_FEET)
      setLocalInches(DEFAULT_INCHES)
      return
    }

    const lowerMinHeight = localFeet < MIN_BODY_FEET

    if (lowerMinHeight) {
      setLocalFeet(MIN_BODY_FEET)
      setLocalInches(0)
      return
    }

    const isOverMaxHeight =
      localFeet > MAX_BODY_FEET ||
      (localFeet === MAX_BODY_FEET && localInches > MAX_BODY_INCHES)

    if (isOverMaxHeight) {
      setLocalFeet(MAX_BODY_FEET)
      setLocalInches(MAX_BODY_INCHES)

      return
    }

    setLocalFeet(feet)
    setLocalInches(inches)
  }, [feet, inches])

  let displayValue = `${feet}'`
  if (inches > 0) {
    displayValue += `${inches}"`
  }

  const shouldDisplayValue = feet > 0 || inches > 0

  return (
    <Fragment>
      <PickerPopupStyle />
      <HeightWheelPickerPopup
        onClose={() => setVisible(false)}
        closeOnMaskClick={true}
        visible={visible}
        bodyStyle={{
          borderRadius: "12px 12px 0 0",
        }}
      >
        <PickerHeader>
          <PickerHeaderTop>
            <PopupIcon />
          </PickerHeaderTop>
          <PickerHeaderBottom>
            <DoneButton
              onClick={() => {
                onChange(localFeet, localInches)
                setVisible(false)
              }}
            >
              Done
            </DoneButton>
          </PickerHeaderBottom>
        </PickerHeader>

        <PickerViewContainer>
          <StyledPickerView
            columns={[feetColumns]}
            value={[localFeet]}
            onChange={(value) => {
              setLocalFeet(value[0] as number)
            }}
          />

          <PickerTextFeet>
            <span>ft</span>
          </PickerTextFeet>

          <StyledPickerViewInches
            columns={[inchesColumn]}
            value={[localInches]}
            onChange={(value) => {
              setLocalInches(value[0] as number)
            }}
          />

          <PickerTextInches>
            <span>in</span>
          </PickerTextInches>

          <PickerSelectedMask />
        </PickerViewContainer>
      </HeightWheelPickerPopup>

      <StyledInputNumberFormItem>
        <StyledLabel>Height</StyledLabel>

        <StyledInput
          placeholder="Enter height"
          $isError={!!isError}
          $disabled={disabled}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!disabled) {
              setVisible(true)
            }
          }}
          onFocus={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          value={shouldDisplayValue ? displayValue : ""}
          onChange={(e) => {}}
          readOnly={true}
        />
      </StyledInputNumberFormItem>
    </Fragment>
  )
}

export default HeightInput
